/* eslint-disable react/forbid-prop-types */
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Select, MenuItem, TextField, Stack,
    IconButton, Tooltip,
} from '@mui/material';
import { useSelector } from 'src/redux/store';
import Iconify from 'src/components/Iconify';
import { hasPractitionerRole } from '../../../utils/practiceUserRoles';

/* ──────────────────────────────────────────────────────────────────────────
   PracticeFieldsBlock
   ───────────────────
   Renders the "Practice fields" section of the Send Forms wizard.

   - Always renders a system-default "Treated by" picker (practitioners only).
   - Additionally enumerates `is_practice_filled=true` PracticeFormFields from
     the currently-selected forms and renders an input per field, where the
     input type depends on `prefill_source`:
       - practice_user     → practitioner Select (same list as Treated by)
       - static            → text input
       - practice_name     → read-only text showing the practice name
       - lead_treatment    → text input pre-filled with the lead's primary
                             treatment if available
   - Per-field "Hidden from patient" or "Visible to patient" badge based on
     each field's `visible_to_patient` flag.
   - Emits `onChange(practiceFieldValues)` returning the dict in the
     backend-expected shape — keys are either `system:treated_by` or the
     `PracticeFormField` PK as a string.

   Empty state: only Treated by is rendered when no form-level practice
   fields are present.
   ────────────────────────────────────────────────────────────────────────── */

// Match the backend ``PracticeFormField.prefill_source`` choices.
const PREFILL_SOURCES = {
    PRACTICE_USER: 'practice_user',
    PRACTICE_NAME: 'practice_name',
    STATIC: 'static',
    LEAD_TREATMENT: 'lead_treatment',
};

const sortPractitioners = (a, b) => {
    const aFirst = (a?.user?.first_name || '').toLowerCase();
    const bFirst = (b?.user?.first_name || '').toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
    const aLast = (a?.user?.last_name || '').toLowerCase();
    const bLast = (b?.user?.last_name || '').toLowerCase();
    return aLast.localeCompare(bLast);
};

const displayForPractitioner = (pu) => {
    const sal = (pu?.user?.salutation || '').trim();
    const fn = (pu?.user?.first_name || '').trim();
    const ln = (pu?.user?.last_name || '').trim();
    return [sal, fn, ln].filter(Boolean).join(' ');
};

const FIELD_BG = '#FFFBEA';
const FIELD_BORDER = '#FDE68A';

const VisibilityBadge = ({ visible }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1, py: '2px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            bgcolor: visible ? '#CAFDF5' : '#FEF3C7',
            color: visible ? '#0078FF' : '#92400E',
        }}
    >
        <Iconify icon={visible ? 'eva:eye-fill' : 'eva:lock-fill'} width={11} />
        {visible ? 'Visible to patient' : 'Hidden from patient'}
    </Box>
);
VisibilityBadge.propTypes = { visible: PropTypes.bool };
VisibilityBadge.defaultProps = { visible: false };

const PracticeFieldsBlock = ({
    selectedForms,
    selectedLead,
    practiceName,
    initialValues,
    onChange,
}) => {
    // Practitioners — surfaced from the existing practiceUser slice. The
    // Redux state shape is ``state.practiceUser.practiceUser.results`` (the
    // outer key is the slice name, the inner ``practiceUser`` is a key on
    // the slice state — see ``src/redux/slices/practiceUser.js:10``).
    //
    // Filter MUST match legacy ``ConsentLink.jsx`` exactly so the list of
    // doctors here is field-level identical to the legacy "Assign to"
    // picker. Concretely:
    //   * Practitioner as any role       — only clinicians appear
    //   * !user.is_superuser             — exclude system / super-admin
    //     accounts that aren't real practitioners (e.g. "Gautam SuperAdmin")
    //   * user.first_name truthy         — drop placeholder / blank-name
    //     rows so the dropdown isn't littered with unusable entries
    // Then sort alphabetically by first/last name for predictability.
    const practiceUserResults = useSelector(
        (state) => state.practiceUser?.practiceUser?.results || [],
    );

    const practitioners = useMemo(() => (
        practiceUserResults
            .filter((pu) => (
                hasPractitionerRole(pu)
                && pu?.user
                && pu.user.first_name
                && !pu.user.is_superuser
            ))
            .slice()
            .sort(sortPractitioners)
    ), [practiceUserResults]);

    // Enumerate practice-filled fields from the selected forms. The form
    // detail endpoint returns sections → fields; we walk both to collect
    // any field where ``is_practice_filled === true``. De-dupe by id in
    // case the same field id appears across multiple selected forms.
    const practiceFields = useMemo(() => {
        const seen = new Map();
        (selectedForms || []).forEach((form) => {
            const sections = form?.sections || [];
            sections.forEach((section) => {
                (section?.fields || []).forEach((field) => {
                    if (field?.is_practice_filled && !seen.has(field.id)) {
                        seen.set(field.id, field);
                    }
                });
            });
        });
        return Array.from(seen.values());
    }, [selectedForms]);

    // Lead's primary (most-recent active) treatment label — used to seed
    // any ``lead_treatment`` field. The Send wizard already filters out
    // closed/archived treatments before passing the lead in; we just
    // pick the first one.
    const primaryTreatmentName = useMemo(() => {
        if (!selectedLead?.lead_treatments) return '';
        const active = selectedLead.lead_treatments.find(
            (t) => t.status !== 'CLOSED' && t.status !== 'ARCHIVED',
        );
        return active?.treatment_item?.name || active?.treatment?.name || '';
    }, [selectedLead]);

    // ── Values dict — keyed exactly the way the backend expects ─────────────
    //   "system:treated_by"            → system default
    //   "<practice_form_field_id>"     → template-declared practice field
    const values = useMemo(() => initialValues || {}, [initialValues]);

    const treatedByValue = values['system:treated_by']?.value || '';

    // On first mount (or when practitioners load) seed Treated by to the
    // first practitioner if the caller hasn't set anything yet. This makes
    // "Treated by" feel like a real default rather than a blank field.
    useEffect(() => {
        if (!onChange) return;
        if (treatedByValue) return;
        if (!practitioners.length) return;
        const first = practitioners[0];
        const next = {
            ...values,
            'system:treated_by': {
                value: first?.user?.id ?? first?.id,
                display: displayForPractitioner(first),
                source: `practice_user:${first?.user?.id ?? first?.id}`,
                visible_to_patient: false,
                label: 'Treated by',
            },
        };
        onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practitioners]);

    // Seed practice field defaults when forms change — pre-fill
    // practice_name fields (read-only) and lead_treatment fields if a
    // treatment is available. Doesn't touch values the user has already
    // entered.
    useEffect(() => {
        if (!onChange) return;
        if (!practiceFields.length) return;
        let dirty = false;
        const next = { ...values };
        practiceFields.forEach((field) => {
            const key = String(field.id);
            if (next[key] && next[key].value) return;
            if (field.prefill_source === PREFILL_SOURCES.PRACTICE_NAME) {
                next[key] = {
                    value: practiceName || '',
                    display: practiceName || '',
                    source: 'practice_name',
                    visible_to_patient: !!field.visible_to_patient,
                    label: field.label || '',
                };
                dirty = true;
            } else if (field.prefill_source === PREFILL_SOURCES.LEAD_TREATMENT && primaryTreatmentName) {
                next[key] = {
                    value: primaryTreatmentName,
                    display: primaryTreatmentName,
                    source: 'lead_treatment',
                    visible_to_patient: !!field.visible_to_patient,
                    label: field.label || '',
                };
                dirty = true;
            }
        });
        if (dirty) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceFields, practiceName, primaryTreatmentName]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleTreatedByChange = (event) => {
        const userId = event.target.value;
        const pu = practitioners.find(
            (p) => String(p?.user?.id ?? p?.id) === String(userId),
        );
        const display = pu ? displayForPractitioner(pu) : '';
        const next = {
            ...values,
            'system:treated_by': {
                value: userId,
                display,
                source: `practice_user:${userId}`,
                visible_to_patient: false,
                label: 'Treated by',
            },
        };
        onChange(next);
    };

    const handlePracticeUserFieldChange = (field, userId) => {
        const pu = practitioners.find(
            (p) => String(p?.user?.id ?? p?.id) === String(userId),
        );
        const display = pu ? displayForPractitioner(pu) : '';
        const next = {
            ...values,
            [String(field.id)]: {
                value: userId,
                display,
                source: `practice_user:${userId}`,
                visible_to_patient: !!field.visible_to_patient,
                label: field.label || '',
            },
        };
        onChange(next);
    };

    const handleStaticFieldChange = (field, value) => {
        const next = {
            ...values,
            [String(field.id)]: {
                value,
                display: value,
                source: 'static',
                visible_to_patient: !!field.visible_to_patient,
                label: field.label || '',
            },
        };
        onChange(next);
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                bgcolor: FIELD_BG,
                border: `1px solid ${FIELD_BORDER}`,
                borderRadius: '10px',
                p: 1.75,
            }}
        >
            {/* Section header inside the block — replaces the verbose
                footer help line with a compact info-icon tooltip
                (FE-7 correction 4). */}
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    mb: 1.25,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11.5px', fontWeight: 700, color: '#92400E',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}
                >
                    Practice fields
                </Typography>
                <Tooltip
                    title="Treated by is always available. Add more practice fields via Form Builder → Field properties → Practice fill."
                    placement="top"
                    arrow
                >
                    <IconButton
                        size="small"
                        aria-label="About practice fields"
                        sx={{
                            p: 0.25, color: '#92400E',
                            '&:hover': { bgcolor: 'transparent', color: '#78350F' },
                        }}
                    >
                        <Iconify icon="eva:info-outline" width={14} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Stack spacing={1.25}>
                {/* ── Treated by — always present (system default) ───────────── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr auto' },
                        gap: 1.25,
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        sx={{ fontSize: '13px', fontWeight: 600, color: '#454F5B' }}
                    >
                        Treated by
                        <Box component="span" sx={{ color: '#FF5630', ml: 0.25 }}>*</Box>
                    </Typography>
                    <Select
                        size="small"
                        fullWidth
                        value={treatedByValue}
                        onChange={handleTreatedByChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Treated by' }}
                        sx={{
                            bgcolor: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
                        }}
                    >
                        {practitioners.length === 0 && (
                            <MenuItem value="" disabled>No practitioners available</MenuItem>
                        )}
                        {practitioners.map((pu) => {
                            const id = pu?.user?.id ?? pu?.id;
                            return (
                                <MenuItem key={id} value={id}>
                                    {displayForPractitioner(pu)}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    <VisibilityBadge visible={false} />
                </Box>

                {/* ── Template-declared practice fields ───────────────────────── */}
                {practiceFields.map((field) => {
                    const key = String(field.id);
                    const current = values[key] || {};
                    const visible = !!field.visible_to_patient;

                    let input = null;
                    if (field.prefill_source === PREFILL_SOURCES.PRACTICE_USER) {
                        input = (
                            <Select
                                size="small"
                                fullWidth
                                value={current.value || ''}
                                onChange={(e) => handlePracticeUserFieldChange(field, e.target.value)}
                                displayEmpty
                                inputProps={{ 'aria-label': field.label || 'Practice field' }}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
                                }}
                            >
                                {practitioners.length === 0 && (
                                    <MenuItem value="" disabled>No practitioners available</MenuItem>
                                )}
                                {practitioners.map((pu) => {
                                    const id = pu?.user?.id ?? pu?.id;
                                    return (
                                        <MenuItem key={id} value={id}>
                                            {displayForPractitioner(pu)}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        );
                    } else if (field.prefill_source === PREFILL_SOURCES.PRACTICE_NAME) {
                        input = (
                            <TextField
                                size="small"
                                fullWidth
                                value={current.value || practiceName || ''}
                                InputProps={{ readOnly: true, 'aria-label': field.label || 'Practice name' }}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: '8px',
                                    '& .MuiOutlinedInput-root': { fontSize: '13px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
                                }}
                            />
                        );
                    } else {
                        // STATIC + LEAD_TREATMENT — both render as a text input.
                        // LEAD_TREATMENT is pre-seeded above; user may still edit.
                        input = (
                            <TextField
                                size="small"
                                fullWidth
                                value={current.value || ''}
                                onChange={(e) => handleStaticFieldChange(field, e.target.value)}
                                placeholder={field.placeholder || 'Optional'}
                                inputProps={{ 'aria-label': field.label || 'Practice field' }}
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: '8px',
                                    '& .MuiOutlinedInput-root': { fontSize: '13px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
                                }}
                            />
                        );
                    }

                    return (
                        <Box
                            key={key}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '140px 1fr auto' },
                                gap: 1.25,
                                alignItems: 'center',
                                pt: 1,
                                borderTop: `1px dashed ${FIELD_BORDER}`,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: '13px', fontWeight: 600, color: '#454F5B' }}
                            >
                                {field.label || 'Practice field'}
                            </Typography>
                            {input}
                            <VisibilityBadge visible={visible} />
                        </Box>
                    );
                })}

            </Stack>
        </Box>
    );
};

PracticeFieldsBlock.propTypes = {
    selectedForms: PropTypes.array,
    selectedLead: PropTypes.object,
    practiceName: PropTypes.string,
    initialValues: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

PracticeFieldsBlock.defaultProps = {
    selectedForms: [],
    selectedLead: null,
    practiceName: '',
    initialValues: null,
};

export default PracticeFieldsBlock;
